import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container, Card, CardContent, TextField, Button, Typography, Box, Alert, CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../api';
import { useLanguage } from '../i18n/LanguageContext';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useLanguage();
  const theme = useTheme();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);

    try {
      const response = await api.post('/token', params);
      const token = response.data?.access_token;
      if (!token || typeof token !== 'string') {
        setError(t.loginFailed);
        return;
      }
      localStorage.setItem('token', token);
      navigate('/app/dashboard');
    } catch {
      setError(t.loginFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {t.appName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t.trackYourHealth}
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>{t.logIn}</Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth size="small" label={t.email} type="email"
                value={username} onChange={(e) => { setUsername(e.target.value); if (error) setError(''); }}
                required sx={{ mb: 2 }} autoComplete="email"
              />
              <TextField
                fullWidth size="small" label={t.password} type="password"
                value={password} onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
                required sx={{ mb: 2 }} autoComplete="current-password"
              />

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ mb: 2 }}>
                {loading ? <CircularProgress size={22} color="inherit" /> : t.logIn}
              </Button>
            </form>

            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              {t.noAccount}{' '}
              <Link to="/register" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600 }}>
                {t.register}
              </Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;
